import { Directionality } from '@angular/cdk/bidi';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import type { AfterContentChecked, AfterContentInit, AfterViewInit, OnDestroy } from '@angular/core';
import { HostBinding } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  Inject,
  Input,
  NgZone,
  Optional,
  QueryList,
  ViewChild,
  ViewEncapsulation,
  InjectionToken,
} from '@angular/core';
import { type CanColor, mixinColor, type ThemePalette } from '../core/common';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import type { MatErrorDirective, MatHintDirective, MatSuffixDirective } from './directives';
import { MAT_HINT, MatLabelDirective, MAT_ERROR, MAT_SUFFIX } from './directives';
import { MatFormFieldControl } from './form-field-control';
import { Platform } from '@angular/cdk/platform';
import type { AbstractControlDirective } from '@angular/forms';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import {
  getMatFormFieldMissingControlError,
  MAT_FORM_FIELD as OLD_MAT_FORM_FIELD,
  matFormFieldAnimations,
} from '@angular/material/form-field';

let nextUniqueId = 0;
const floatingLabelScale = 0.75;
const outlineGapPadding = 5;

/**
 * Boilerplate for applying mixins to MatFormField.
 */
const _MatFormFieldBase = mixinColor(
  class {
    constructor(public _elementRef: ElementRef) {}
  },
  'primary',
);

/**
 * Possible appearance styles for the form field.
 */
export type MatFormFieldAppearance = 'legacy' | 'standard' | 'outline';

/**
 * Possible values for the "floatLabel" form field input.
 */
export type FloatLabelType = 'always' | 'never' | 'auto';

/**
 * Represents the default options for the form field that can be configured
 * using the `MAT_FORM_FIELD_DEFAULT_OPTIONS` injection token.
 */
export interface MatFormFieldDefaultOptions {
  /** Default form field appearance style. */
  appearance?: MatFormFieldAppearance;
  /** Default color of the form field. */
  color?: ThemePalette;
  /** Whether the required marker should be hidden by default. */
  hideRequiredMarker?: boolean;
  /**
   * Whether the label for form fields should by default float `always`,
   * `never`, or `auto` (only when necessary).
   */
  floatLabel?: FloatLabelType;
}

/**
 * Injection token that can be used to configure the
 * default options for all form field within an app.
 */
export const MAT_FORM_FIELD_DEFAULT_OPTIONS = new InjectionToken<MatFormFieldDefaultOptions>(
  'MAT_FORM_FIELD_DEFAULT_OPTIONS',
);

/**
 * Injection token that can be used to inject an instances of `MatFormField`. It serves
 * as alternative token to the actual `MatFormField` class which would cause unnecessary
 * retention of the `MatFormField` class and its component metadata.
 */
export const MAT_FORM_FIELD = new InjectionToken<MatFormFieldComponent>('MatFormField');

/**
 * Container for form controls that applies Material Design styling and behavior.
 */
@Component({
  selector: 'app-mat-form-field',
  exportAs: 'matFormField',
  templateUrl: 'form-field.component.html',
  // MatInput is a directive and can't have styles, so we need to include its styles here
  // in form-field-input.css. The MatInput styles are fairly minimal so it shouldn't be a
  // big deal for people who aren't using MatInput.
  styleUrls: [
    'styles/form-field.component.scss',
    'styles/form-field-input.scss',
    'styles/form-field-legacy.scss',
    'styles/form-field-outline.scss',
    'styles/form-field-standard.scss',
  ],
  animations: [matFormFieldAnimations.transitionMessages],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_FORM_FIELD, useExisting: MatFormFieldComponent },
    { provide: OLD_MAT_FORM_FIELD, useExisting: MAT_FORM_FIELD },
  ],
})
export class MatFormFieldComponent
  extends _MatFormFieldBase
  implements AfterContentInit, AfterContentChecked, AfterViewInit, OnDestroy, CanColor
{
  @HostBinding('class') readonly hostClass = 'mat-form-field';
  @HostBinding('attrs.ngSkipHydration') readonly ngSkipHydration = '';
  @ContentChild(MatFormFieldControl) readonly _control: MatFormFieldControl<any>;

  @ViewChild('connectionContainer', { static: true }) protected _connectionContainerRef: ElementRef<HTMLElement>;
  @ViewChild('inputContainer') protected _inputContainerRef: ElementRef<HTMLElement>;
  @ContentChild(MatLabelDirective) protected _labelChild: MatLabelDirective;

  @ContentChildren(MAT_ERROR, { descendants: true }) protected _errorChildren: QueryList<MatErrorDirective>;
  @ContentChildren(MAT_HINT, { descendants: true }) protected _hintChildren: QueryList<MatHintDirective>;
  @ContentChildren(MAT_SUFFIX, { descendants: true }) protected _suffixChildren: QueryList<MatSuffixDirective>;
  @ViewChild('label') private _label: ElementRef<HTMLElement>;

  protected _appearance: MatFormFieldAppearance;

  // Unique id for the hint label.
  protected readonly _hintLabelId: string = `mat-hint-${nextUniqueId++}`;

  // Unique id for the label element.
  protected readonly _labelId = `mat-form-field-label-${nextUniqueId++}`;

  /** State of the mat-hint and mat-error animations. */
  protected _subscriptAnimationState: string = '';

  /** Whether the Angular animations are enabled. */
  protected _animationsEnabled: boolean;

  /**
   * Whether the outline gap needs to be calculated
   * immediately on the next change detection run.
   */
  private _outlineGapCalculationNeededImmediately = false;

  /** Whether the outline gap needs to be calculated next time the zone has stabilized. */
  private _outlineGapCalculationNeededOnStable = false;

  /** Override for the logic that disables the label animation in certain cases. */
  private _showAlwaysAnimate = false;

  private readonly _destroyed = new Subject<void>();
  private _hideRequiredMarker = false;
  private _hintLabel = '';
  private _floatLabel: FloatLabelType;

  /** The form field appearance style. */
  @Input()
  get appearance(): MatFormFieldAppearance {
    return this._appearance;
  }
  set appearance(value: MatFormFieldAppearance) {
    const oldValue = this._appearance;

    this._appearance = value ?? this._defaults?.appearance ?? 'legacy';

    if (this._appearance === 'outline' && oldValue !== value) {
      this._outlineGapCalculationNeededOnStable = true;
    }
  }

  /** Whether the required marker should be hidden. */
  @Input()
  get hideRequiredMarker(): boolean {
    return this._hideRequiredMarker;
  }
  set hideRequiredMarker(value: BooleanInput) {
    this._hideRequiredMarker = coerceBooleanProperty(value);
  }

  /** Text for the form field hint. */
  @Input()
  get hintLabel(): string {
    return this._hintLabel;
  }
  set hintLabel(value: string) {
    this._hintLabel = value;
    this._syncDescribedByIds();
  }

  /**
   * Whether the label should always float, never float or float as the user types.
   *
   * Note: only the legacy appearance supports the `never` option. `never` was originally added as a
   * way to make the floating label emulate the behavior of a standard input placeholder. However
   * the form field now supports both floating labels and placeholders. Therefore in the non-legacy
   * appearances the `never` option has been disabled in favor of just using the placeholder.
   */
  @Input()
  get floatLabel(): FloatLabelType {
    return this.appearance !== 'legacy' && this._floatLabel === 'never' ? 'auto' : this._floatLabel;
  }
  set floatLabel(value: FloatLabelType) {
    if (value !== this._floatLabel) {
      this._floatLabel = value || this._getDefaultFloatLabelState();
      this._changeDetectorRef.markForCheck();
    }
  }

  constructor(
    elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    @Optional() private _dir: Directionality,
    @Optional()
    @Inject(MAT_FORM_FIELD_DEFAULT_OPTIONS)
    private _defaults: MatFormFieldDefaultOptions,
    private _platform: Platform,
    private _ngZone: NgZone,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) _animationMode: string,
  ) {
    super(elementRef);

    this.floatLabel = this._getDefaultFloatLabelState();
    this._animationsEnabled = _animationMode !== 'NoopAnimations';

    // Set the default through here so we invoke the setter on the first run.
    this.appearance = _defaults?.appearance || 'legacy';
    if (_defaults) {
      this._hideRequiredMarker = Boolean(_defaults.hideRequiredMarker);
      if (_defaults.color) {
        this.color = this.defaultColor = _defaults.color;
      }
    }
  }

  /**
   * Gets the id of the label element. If no label is present, returns `null`.
   * This is used to set the `aria-labelledby` attribute on the form field control.
   */
  public getLabelId(): string | null {
    return this._hasFloatingLabel() ? this._labelId : null;
  }

  /**
   * Gets an ElementRef for the element that a overlay attached to the form field should be
   * positioned relative to.
   */
  getConnectedOverlayOrigin(): ElementRef {
    return this._connectionContainerRef ?? this._elementRef;
  }

  ngAfterContentInit() {
    this._validateControlChild();

    const control = this._control;

    if (control.controlType) {
      this._elementRef.nativeElement.classList.add(`mat-form-field-type-${control.controlType}`);
    }

    // Subscribe to changes in the child control state in order to update the form field UI.
    control.stateChanges.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });

    // Run change detection if the value changes.
    if (control.ngControl?.valueChanges) {
      control.ngControl.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => this._changeDetectorRef.markForCheck());
    }

    // Note that we have to run outside of the `NgZone` explicitly,
    // in order to avoid throwing users into an infinite loop
    // if `zone-patch-rxjs` is included.
    this._ngZone.runOutsideAngular(() => {
      this._ngZone.onStable.pipe(takeUntil(this._destroyed)).subscribe(() => {
        if (this._outlineGapCalculationNeededOnStable) {
          this.updateOutlineGap();
        }
      });
    });

    // Run change detection and update the outline if the suffix or prefix changes.
    this._suffixChildren.changes.subscribe(() => {
      this._outlineGapCalculationNeededOnStable = true;
      this._changeDetectorRef.markForCheck();
    });

    // Re-validate when the number of hints changes.
    this._hintChildren.changes.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });

    // Update the aria-described by when the number of errors changes.
    this._errorChildren.changes.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });

    if (this._dir) {
      this._dir.change.pipe(takeUntil(this._destroyed)).subscribe(() => {
        if (typeof requestAnimationFrame === 'function') {
          this._ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => this.updateOutlineGap());
          });
        } else {
          this.updateOutlineGap();
        }
      });
    }
  }

  ngAfterContentChecked() {
    this._validateControlChild();
    if (this._outlineGapCalculationNeededImmediately) {
      this.updateOutlineGap();
    }
  }

  ngAfterViewInit() {
    // Avoid animations on load.
    this._subscriptAnimationState = 'enter';
    this._changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  public _hasLabel() {
    return !!this._labelChild;
  }

  /**
   * Determines whether a class from the AbstractControlDirective
   * should be forwarded to the host element.
   */
  protected _shouldForward(prop: keyof AbstractControlDirective): boolean {
    const control = this._control ? this._control.ngControl : null;
    return !!control?.[prop];
  }

  protected _hasPlaceholder() {
    return !!this._control?.placeholder;
  }

  /** Whether the floating label should always float or not. */
  protected _shouldAlwaysFloat(): boolean {
    return this.floatLabel === 'always' && !this._showAlwaysAnimate;
  }

  /** Whether the label can float or not. */
  @HostBinding('class.mat-form-field-can-float')
  protected get canLabelFloat(): boolean {
    return this.floatLabel !== 'never';
  }

  @HostBinding('class.mat-form-field-should-float')
  protected get shouldLabelFloat() {
    return this.canLabelFloat && (this._control?.shouldLabelFloat || this._shouldAlwaysFloat());
  }

  @HostBinding('class.mat-form-field-hide-placeholder')
  protected get hideControlPlaceholder() {
    // In the legacy appearance the placeholder is promoted to a label if no label is given.
    return (this.appearance === 'legacy' && !this._hasLabel()) || (this._hasLabel() && !this.shouldLabelFloat);
  }

  @HostBinding('class.mat-form-field-has-label')
  protected get hasFloatingLabel() {
    // In the legacy appearance the placeholder is promoted to a label if no label is given.
    return this._hasLabel() || (this.appearance === 'legacy' && this._hasPlaceholder());
  }

  @HostBinding('class.mat-form-field-appearance-standard')
  protected get isAppearanceStandard() {
    return this.appearance === 'standard';
  }

  @HostBinding('class.mat-form-field-appearance-outline')
  protected get isAppearanceOutline() {
    return this.appearance === 'outline';
  }

  @HostBinding('class.mat-form-field-appearance-legacy')
  protected get isAppearanceLegacy() {
    return this.appearance === 'legacy';
  }

  @HostBinding('class.mat-form-field-invalid')
  protected get isFieldInvalid() {
    return this._control?.errorState;
  }

  @HostBinding('class.mat-form-field-disabled')
  protected get isDisabled() {
    return this._control?.disabled;
  }

  @HostBinding('class.mat-form-field-autofilled')
  protected get isAutofilled() {
    return this._control?.autofilled;
  }

  @HostBinding('class.mat-focused')
  protected get isFocused() {
    return this._control?.focused;
  }

  @HostBinding('class.ng-untouched')
  protected get isUntouched() {
    return this._shouldForward('untouched');
  }

  @HostBinding('class.ng-touched')
  protected get isTouched() {
    return this._shouldForward('touched');
  }

  @HostBinding('class.ng-pristine')
  protected get isPristine() {
    return this._shouldForward('pristine');
  }

  @HostBinding('class.ng-dirty')
  protected get isDirty() {
    return this._shouldForward('dirty');
  }

  @HostBinding('class.ng-valid')
  protected get isValid() {
    return this._shouldForward('valid');
  }

  @HostBinding('class.ng-invalid')
  protected get isInvalid() {
    return this._shouldForward('invalid');
  }

  @HostBinding('class.ng-pending')
  protected get isPending() {
    return this._shouldForward('pending');
  }

  @HostBinding('class._mat-animation-noopable')
  protected get isAnimationDisabled() {
    return !this._animationsEnabled;
  }

  /** Determines whether to display hints or errors. */
  protected _getDisplayedMessages(): 'error' | 'hint' {
    return this._errorChildren && this._errorChildren.length > 0 && this._control.errorState ? 'error' : 'hint';
  }

  /** Throws an error if the form field's control is missing. */
  protected _validateControlChild() {
    if (!this._control) {
      throw getMatFormFieldMissingControlError();
    }
  }

  /**
   * Updates the width and position of the gap in the outline. Only relevant for the outline
   * appearance.
   */
  protected updateOutlineGap() {
    const labelEl = this._label ? this._label.nativeElement : null;
    const container = this._connectionContainerRef.nativeElement;
    const outlineStartSelector = '.mat-form-field-outline-start';
    const outlineGapSelector = '.mat-form-field-outline-gap';

    // getBoundingClientRect isn't available on the server.
    if (this.appearance !== 'outline' || !this._platform.isBrowser) {
      return;
    }

    // If there is no content, set the gap elements to zero.
    if (!labelEl?.children.length || !labelEl.textContent!.trim()) {
      const gapSelector = `${outlineStartSelector}, ${outlineGapSelector}`;
      const gapElements: NodeListOf<HTMLElement> = container.querySelectorAll(gapSelector);
      for (let i = 0; i < gapElements.length; i++) {
        gapElements.item(i).style.width = '0';
      }
      return;
    }

    // If the element is not present in the DOM, the outline gap will need to be calculated
    // the next time it is checked and in the DOM.
    if (!this._isAttachedToDOM()) {
      this._outlineGapCalculationNeededImmediately = true;
      return;
    }

    let startWidth = 0;
    let gapWidth = 0;

    const startEls: NodeListOf<HTMLElement> = container.querySelectorAll(outlineStartSelector);
    const gapEls: NodeListOf<HTMLElement> = container.querySelectorAll(outlineGapSelector);

    if (this._label?.nativeElement.children.length) {
      const containerRect = container.getBoundingClientRect();

      // If the container's width and height are zero, it means that the element is
      // invisible and we can't calculate the outline gap. Mark the element as needing
      // to be checked the next time the zone stabilizes. We can't do this immediately
      // on the next change detection, because even if the element becomes visible,
      // the `ClientRect` won't be recalculated immediately. We reset the
      // `_outlineGapCalculationNeededImmediately` flag some we don't run the checks twice.
      if (containerRect.width === 0 && containerRect.height === 0) {
        this._outlineGapCalculationNeededOnStable = true;
        this._outlineGapCalculationNeededImmediately = false;
        return;
      }

      ({ gapWidth, startWidth } = this._getStartAndGapWidth(containerRect, labelEl));
    }

    for (let i = 0; i < startEls.length; i++) {
      startEls.item(i).style.width = `${startWidth}px`;
    }
    for (let i = 0; i < gapEls.length; i++) {
      gapEls.item(i).style.width = `${gapWidth}px`;
    }

    this._outlineGapCalculationNeededOnStable = this._outlineGapCalculationNeededImmediately = false;
  }

  private _getStartAndGapWidth(containerRect: DOMRect, labelEl: HTMLElement): { startWidth: number; gapWidth: number } {
    const containerStart = this._getStartEnd(containerRect);
    const labelChildren = labelEl.children;
    const labelStart = this._getStartEnd(labelChildren[0].getBoundingClientRect());

    let labelWidth = 0;
    for (let i = 0; i < labelChildren.length; i++) {
      labelWidth += (labelChildren.item(i) as HTMLElement).offsetWidth;
    }

    const startWidth = Math.abs(labelStart - containerStart) - outlineGapPadding;
    const gapWidth = labelWidth > 0 ? labelWidth * floatingLabelScale + outlineGapPadding * 2 : 0;
    return { startWidth, gapWidth };
  }

  /** Gets the default float label state. */
  private _getDefaultFloatLabelState(): FloatLabelType {
    return this._defaults?.floatLabel ?? 'auto';
  }

  /**
   * Sets the list of element IDs that describe the child control. This allows the control to update
   * its `aria-describedby` attribute accordingly.
   */
  private _syncDescribedByIds() {
    if (!this._control) {
      return;
    }

    const ids: string[] = [];
    if (this._control.userAriaDescribedBy && typeof this._control.userAriaDescribedBy === 'string') {
      ids.push(...this._control.userAriaDescribedBy.split(' '));
    }
    if (this._getDisplayedMessages() === 'hint') {
      const startHint = this._hintChildren ? this._hintChildren.find((hint) => hint.align === 'start') : null;
      const endHint = this._hintChildren ? this._hintChildren.find((hint) => hint.align === 'end') : null;

      if (startHint) {
        ids.push(startHint.id);
      } else if (this._hintLabel) {
        ids.push(this._hintLabelId);
      }

      if (endHint) {
        ids.push(endHint.id);
      }
    } else if (this._errorChildren) {
      ids.push(...this._errorChildren.map((error) => error.id));
    }
    this._control.setDescribedByIds(ids);
  }

  /** Gets the start end of the rect considering the current directionality. */
  private _getStartEnd(rect: ClientRect): number {
    return this._dir && this._dir.value === 'rtl' ? rect.right : rect.left;
  }

  /** Checks whether the form field is attached to the DOM. */
  private _isAttachedToDOM(): boolean {
    const element: HTMLElement = this._elementRef.nativeElement;

    if (element.getRootNode) {
      const rootNode = element.getRootNode();
      // If the element is inside the DOM the root node will be either the document
      // or the closest shadow root, otherwise it'll be the element itself.
      return rootNode && rootNode !== element;
    }

    // Otherwise fall back to checking if it's in the document. This doesn't account for
    // shadow DOM, however browser that support shadow DOM should support `getRootNode` as well.
    return document.documentElement!.contains(element);
  }

  private _hasFloatingLabel() {
    // In the legacy appearance the placeholder is promoted to a label if no label is given.
    return this._hasLabel() || (this.appearance === 'legacy' && this._hasPlaceholder());
  }
}
